import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Table, Button, Row, Col, Form, Badge } from 'react-bootstrap';
import { useAdminListPartCategoriesQuery } from '../../slices/productsApiSlice';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import Paginate from '../../Components/Paginate';
import ProductLocationsSummary from '../../Components/admin/ProductLocationsSummary';
import AdminBarcodeLookup from '../../Components/admin/AdminBarcodeLookup';
import { toast } from 'react-toastify';
import { getProductDisplayName } from '../../utils/productTitle';
import { PRODUCT_STATUS_OPTIONS, getProductStatusOption } from '../../utils/productStatus';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useDeleteProductMutation,
  useUpdateProductMutation,
} from '../../slices/productsApiSlice';

const ProductListScreen = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { pageNumber } = useParams();

  const category = sp.get('category') || '';
  const status = sp.get('status') || '';
  const descriptionQuality = sp.get('descriptionQuality') || '';

  // NEW: read keyword from URL + local state
  const [keyword, setKeyword] = useState(sp.get('keyword') || '');

  useEffect(() => {
    setKeyword(sp.get('keyword') || '');
  }, [sp]);

  const { data: catList = [] } = useAdminListPartCategoriesQuery();

  const { data, isLoading, error, refetch } = useGetProductsQuery({
    pageNumber,
    ...(category ? { category } : {}),
    ...(keyword ? { keyword } : {}), // NEW: send keyword to API
    ...(status ? { status } : {}),
    ...(descriptionQuality ? { descriptionQuality } : {}),
  });

  // Rebuild the list URL from the current filters, overriding the changed ones.
  const goToList = (changes = {}) => {
    const filters = { category, keyword: keyword.trim(), status, descriptionQuality, ...changes };
    const params = new URLSearchParams(Object.entries(filters).filter(([, value]) => value));
    navigate(`/admin/productlist${params.toString() ? `?${params}` : ''}`);
  };

  const [createProduct, { isLoading: loadingCreate }] = useCreateProductMutation();
  const [deleteProduct, { isLoading: loadingDelete }] = useDeleteProductMutation();
  const [updateProduct, { isLoading: loadingStatus }] = useUpdateProductMutation();

  const deleteHandler = async (id) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this product? This cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await deleteProduct(id).unwrap();
      toast.success('Product deleted');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const createProductHandler = async () => {
    if (window.confirm('Are you sure you want to create a new product?')) {
      try {
        const created = await createProduct().unwrap();
        toast.success('Draft product created');
        navigate(`/admin/product/${created._id}/edit`);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // NEW: search submit handler
  const searchHandler = (e) => {
    e.preventDefault();
    // reset to main list (page 1) with filters in query string
    goToList();
  };

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Products {category ? `— ${category}` : ''}</h1>
        </Col>
        <Col className="text-end">
          <Button className="btn-sm m-3" onClick={createProductHandler}>
            <FaEdit /> Create product
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        {/* Category filter */}
        <Col md={3}>
          <Form.Select
            value={category}
            onChange={(e) => goToList({ category: e.target.value })}
          >
            <option value="">All categories</option>
            {catList.map((c) => (
              <option key={c._id || c.key || c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3} className="mt-2 mt-md-0">
          <Form.Select
            value={status}
            onChange={(e) => goToList({ status: e.target.value })}
          >
            <option value="">All statuses</option>
            {PRODUCT_STATUS_OPTIONS.slice().reverse().map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
        </Col>

        {/* Products Google is likely to skip: missing, placeholder or duplicated descriptions */}
        <Col md={2} className="mt-2 mt-md-0">
          <Form.Select
            value={descriptionQuality}
            onChange={(e) => goToList({ descriptionQuality: e.target.value })}
            aria-label="Filter by description quality"
          >
            <option value="">All descriptions</option>
            <option value="weak">Weak descriptions</option>
          </Form.Select>
        </Col>

        {/* NEW: Search box with placeholder */}
        <Col md={3} className="mt-2 mt-md-0">
          <Form onSubmit={searchHandler}>
            <Form.Control
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search by name, SKU, brand…"
            />
          </Form>
        </Col>

        <Col md="auto" className="d-flex align-items-center mt-2 mt-md-0">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/admin/products')}
            className="ms-md-2"
          >
            Category hub
          </Button>
        </Col>
      </Row>

      <AdminBarcodeLookup />

      {loadingCreate && <Loader />}
      {(loadingDelete || loadingStatus) && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm align-middle">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Barcode</th>
                <th>Location</th>
                <th>PRICE</th>
                <th className="text-center">QTY</th>
                <th className="text-center">Shelf Qty</th>
                <th className="text-center">INVENTORY QTY</th>
                <th>CATEGORY</th>
                <th>BRAND</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((product) => {
                const qty = product.stockQty ?? 0;
                const shelfQty = product.countInStock ?? product.stockQty ?? 0;
                const backroomQty = product.inventoryQty ?? 0;
                const inventoryLocations = Array.isArray(product.inventoryLocations) ? product.inventoryLocations : [];
                const statusOption = getProductStatusOption(product);

                return (
                  <tr key={product._id}>
                    <td className="small font-monospace">
                      {product.sku?.trim() ||
                        product.partNumber?.trim() ||
                        product._id?.slice(-8)}
                    </td>
                    <td>{product.displayName || getProductDisplayName(product)}</td>
                    <td className="small">{inventoryLocations[0]?.barcode || product.barcode || '-'}</td>
                    <td className="small"><ProductLocationsSummary product={product} compact /></td>
                    <td>${product.price}</td>
                    <td className="text-center">
                      <Badge
                        bg={
                          qty <= 0 ? 'danger' : qty <= 5 ? 'warning' : 'success'
                        }
                      >
                        {qty}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge
                        bg={
                          shelfQty <= 0 ? 'danger' : shelfQty <= 5 ? 'warning' : 'success'
                        }
                      >
                        {shelfQty}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="secondary">{backroomQty}</Badge>
                    </td>
                    <td>{product.category}</td>
                    <td>{product.brand}</td>
                    <td className="text-center text-nowrap align-middle">
                      <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
                        <Form.Select
                          size="sm"
                          className="flex-shrink-0"
                          style={{ width: '125px' }}
                          value={product.status || 'active'}
                          aria-label={`Change status for ${product.displayName || product.name}`}
                          onChange={async (e) => {
                            try {
                              await updateProduct({ _id: product._id, status: e.target.value }).unwrap();
                              toast.success(`Status changed to ${PRODUCT_STATUS_OPTIONS.find((option) => option.value === e.target.value)?.label}.`);
                              refetch();
                            } catch (err) {
                              toast.error(err?.data?.message || err.error || 'Status update failed');
                            }
                          }}
                        >
                          {PRODUCT_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Form.Select>
                        <Badge
                          bg={statusOption.badge}
                          text={statusOption.value === 'comingSoon' ? 'dark' : undefined}
                          className="d-inline-flex align-items-center justify-content-center flex-shrink-0 text-nowrap m-0"
                        >
                          {statusOption.label}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <Link to={`/admin/product/${product._id}/edit`}>
                        <Button variant="light" className="btn-sm mx-2">
                          <FaEdit />
                        </Button>
                      </Link>
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => deleteHandler(product._id)}
                      >
                        <FaTrash style={{ color: 'white' }} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} />
        </>
      )}
    </>
  );
};

export default ProductListScreen;
